import { Col, DatePicker, Form, Input, Row, Select, Spin } from "antd";
import moment from "moment/moment";
import { useState } from "react";
import DeactivationConfirmationModal from "../../common/components/AreasPositions/DeactivationConfirmationModal";
import {
  GutteredRow,
  ResponsiveCol,
} from "../../common/components/AreasPositions/Grid";
import { useSeniorityLevels, useSettingValue } from "../../utils/Hooks";
import { maxValueRule } from "../../utils/input";
import { MoneyField } from "../../utils/MoneyField";
import { validateSalaryRange } from "../../utils/validations";

const PositionEditForm = ({ form, position }) => {
  const [endDateState, setEndDateState] = useState({
    open: false,
    value: undefined,
    agreement: false,
  });
  const { TextArea } = Input;
  const start_date = moment(new Date(position.current_record.timespan.lower));

  const { seniorityLevels, isLoading } = useSeniorityLevels();
  const isSalaryRangeEnabled = useSettingValue(
    "T011_POSITION_SALARY_RANGE_ENABLE",
  );

  const onOk = () => {
    setEndDateState((oldState) => ({
      ...oldState,
      agreement: true,
      open: true,
    }));
  };

  return (
    <Form layout={"vertical"} form={form}>
      <GutteredRow>
        <ResponsiveCol>
          <Form.Item
            label="Nome do Cargo:"
            name="name"
            initialValue={position.name}
            rules={[
              { required: true, message: "Por favor insira o nome do cargo." },
              maxValueRule(200),
            ]}
          >
            <Input />
          </Form.Item>
        </ResponsiveCol>
        <ResponsiveCol>
          <Form.Item
            label="Código:"
            name="code"
            initialValue={position.code}
            rules={[
              {
                required: true,
                message: "Por favor insira o código do cargo.",
              },
              maxValueRule(100),
            ]}
          >
            <Input />
          </Form.Item>
        </ResponsiveCol>
      </GutteredRow>
      <GutteredRow>
        <ResponsiveCol>
          <Form.Item
            label="Nível de senioridade:"
            name="seniority_level"
            initialValue={position.seniority_level?.key}
          >
            <Select
              allowClear
              placeholder="Selecione"
              notFoundContent={
                isLoading ? <Spin size="small" /> : "Nenhum item encontrado"
              }
              options={seniorityLevels?.results.map((option) => ({
                value: option.key,
                label: option.level,
              }))}
            />
          </Form.Item>
        </ResponsiveCol>
        <ResponsiveCol>
          <Form.Item
            label="Categoria:"
            name="category"
            initialValue={position.category}
            rules={[maxValueRule(200)]}
          >
            <Input />
          </Form.Item>
        </ResponsiveCol>
      </GutteredRow>
      <GutteredRow>
        <ResponsiveCol>
          <Form.Item
            label="Data de criação"
            name="start_date"
            initialValue={start_date}
            rules={[
              {
                required: true,
                message: "Por favor insira a data de criação.",
              },
            ]}
          >
            <DatePicker
              placeholder={"Selecione uma data"}
              format={"DD/MM/YYYY"}
              disabledDate={(current) => {
                return (
                  current &&
                  form.getFieldValue("end_date") &&
                  current > form.getFieldValue("end_date").startOf("day")
                );
              }}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </ResponsiveCol>
        <ResponsiveCol>
          <Form.Item
            label="Data de fim do cargo"
            name="end_date"
            rules={[
              {
                validator: async (_, endDate) => {
                  let startDatetime = form.getFieldValue("start_date");

                  if (endDate && endDate <= startDatetime) {
                    return Promise.reject(
                      "A data de fim do cargo deve ser maior que a data de inicio!",
                    );
                  }
                },
              },
            ]}
          >
            <DatePicker
              placeholder={"Selecione uma data"}
              format={"DD/MM/YYYY"}
              value={endDateState.value}
              disabledDate={(current) => {
                return (
                  current &&
                  current < form.getFieldValue("start_date").endOf("day")
                );
              }}
              onClick={() =>
                endDateState.agreement
                  ? setEndDateState((oldState) => ({
                      ...oldState,
                      open: true,
                    }))
                  : DeactivationConfirmationModal({
                      title: "Inativar Cargo?",
                      content:
                        "Ao definir uma data final do cargo, ele se torna inativo e não será possível atribuir à funcionários. Tem certeza que deseja continuar?",
                      onOk,
                    })
              }
              onChange={(value) =>
                setEndDateState((oldState) => ({
                  ...oldState,
                  open: false,
                  value: value,
                }))
              }
              onBlur={() =>
                setEndDateState((oldState) => ({ ...oldState, open: false }))
              }
              open={endDateState.open}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </ResponsiveCol>
      </GutteredRow>
      {isSalaryRangeEnabled && (
        <Form.Item label="Faixa de remuneração:" name="salary_range">
          <GutteredRow>
            <ResponsiveCol>
              <Form.Item
                name="min_salary"
                initialValue={position.min_salary}
                noStyle
              >
                <MoneyField
                  prefix="R$"
                  style={{ width: "100%" }}
                  step={100}
                  precision={4}
                  placeholder="Mínimo"
                  onChange={() => validateSalaryRange(form)}
                />
              </Form.Item>
            </ResponsiveCol>
            <ResponsiveCol>
              <Form.Item
                name="max_salary"
                initialValue={position.max_salary}
                noStyle
              >
                <MoneyField
                  style={{ width: "100%" }}
                  min={1}
                  step={100}
                  precision={4}
                  placeholder="Máximo"
                  onChange={() => validateSalaryRange(form)}
                />
              </Form.Item>
            </ResponsiveCol>
          </GutteredRow>
        </Form.Item>
      )}
      <Row>
        <Col span={24}>
          <Form.Item
            label="Descrição:"
            name="description"
            initialValue={position.description}
          >
            <TextArea rows={4} />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default PositionEditForm;
